import { useTranslation } from 'react-i18next'
import { CompanyForm } from '../company/company.form.hook'
import { PersonForm } from '../person/person.form.hook'
import { GenderType, useCountriesLazyQuery, useMunicipalitiesLazyQuery } from 'src/api/api.generated'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'

const CONSONANTS_REGEX = /[AEIOU]/gi
const VOWELS_REGEX = /[BCDFGHJKLMNPQRSTVXZWY]/gi
const NAMES_REGEX = /[^A-Z0-9]+/gi
const MONTH_TO_FISCAL_CODE = ['A', 'B', 'C', 'D', 'E', 'H', 'L', 'M', 'P', 'R', 'S', 'T']
const ALPHABET = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']

export default function useGenerateItalianFiscalCode() {
  const { t } = useTranslation()
  const showError = useErrorSnackBar()
  const [getCountry] = useCountriesLazyQuery({
    onError: showError,
  })
  const [getMunicipality] = useMunicipalitiesLazyQuery({
    onError: showError,
  })

  async function generateItalianFiscalCode(customer: PersonForm | CompanyForm) {
    if ('companyName' in customer) {
      return {
        error: t('pms.common.customer.company_not_supported'),
      }
    } else {
      const { firstName, lastName, birthDate, birthAddress, gender } = customer
      if (birthAddress?.countryId) {
        const { data: countryData } = await getCountry({
          fetchPolicy: 'cache-first',
          variables: { filter: { isoCode: { eq: 'IT' } } },
        })
        const countryId = countryData?.countries?.[0]?.id

        if (birthAddress?.countryId !== countryId) {
          return {
            error: t('pms.common.customer.foreign_country_not_supported'),
          }
        }
      }

      const missingFields = []
      if (firstName.length < 2) {
        missingFields.push(t('pms.common.customer.firstName'))
      }
      if (lastName.length < 2) {
        missingFields.push(t('pms.common.customer.lastName'))
      }
      if (!gender) {
        missingFields.push(t('pms.common.customer.gender'))
      }
      if (!birthDate) {
        missingFields.push(t('pms.common.customer.birth_date'))
      }
      if (!birthAddress?.municipalityId) {
        missingFields.push(t('pms.common.customer.birth_municipality'))
      }
      if (missingFields.length > 0) {
        return {
          error: t('pms.common.customer.required_fields', { fields: missingFields.join(', ') }),
        }
      }

      let fiscal_code = ''

      const lastNameConsonants = lastName.trim().replace(NAMES_REGEX, '').toUpperCase().replace(CONSONANTS_REGEX, '') // extract the consonants from the last name
      const lastNameVowels = lastName.trim().replace(NAMES_REGEX, '').toUpperCase().replace(VOWELS_REGEX, '') // extract the vowels from the last name

      if (lastNameConsonants.length >= 3) {
        // add the first 3 consonants to the fiscal code
        fiscal_code += lastNameConsonants[0]
        fiscal_code += lastNameConsonants[1]
        fiscal_code += lastNameConsonants[2]
      } else if (lastNameConsonants.length >= 2 && lastNameVowels.length >= 1) {
        // add the first 2 consonants and the first vowel
        fiscal_code += lastNameConsonants[0]
        fiscal_code += lastNameConsonants[1]
        fiscal_code += lastNameVowels[0]
      } else if (lastNameConsonants.length >= 1 && lastNameVowels.length >= 2) {
        // add the first consonant and the first 2 vowels
        fiscal_code += lastNameConsonants[0]
        fiscal_code += lastNameVowels[0]
        fiscal_code += lastNameVowels[1]
      } else if (lastNameConsonants.length == 1 && lastNameVowels.length == 1) {
        // add the first consonant, the first vowel and an "X"
        fiscal_code += lastNameConsonants[0]
        fiscal_code += lastNameVowels[0]
        fiscal_code += 'X'
      } else if (lastNameVowels.length == 2) {
        // add the first 2 vowels and an "X"
        fiscal_code += lastNameVowels[0]
        fiscal_code += lastNameVowels[1]
        fiscal_code += 'X'
      }

      const firstNameConsonants = firstName.trim().replace(NAMES_REGEX, '').toUpperCase().replace(CONSONANTS_REGEX, '') // extract the consonants from the name
      const firstNameVowels = firstName.trim().replace(NAMES_REGEX, '').toUpperCase().replace(VOWELS_REGEX, '') // extract the vowels from the name

      if (firstNameConsonants.length >= 4) {
        // add the first, third and fourth consonants
        fiscal_code += firstNameConsonants[0]
        fiscal_code += firstNameConsonants[2]
        fiscal_code += firstNameConsonants[3]
      } else if (firstNameConsonants.length >= 3) {
        // add the first 3 consonants
        fiscal_code += firstNameConsonants[0]
        fiscal_code += firstNameConsonants[1]
        fiscal_code += firstNameConsonants[2]
      } else if (firstNameConsonants.length >= 2 && firstNameVowels.length >= 1) {
        // add the first 2 consonants and the first vowel
        fiscal_code += firstNameConsonants[0]
        fiscal_code += firstNameConsonants[1]
        fiscal_code += firstNameVowels[0]
      } else if (firstNameConsonants.length >= 1 && firstNameVowels.length >= 2) {
        // add the first consonant and the first 2 vowels
        fiscal_code += firstNameConsonants[0]
        fiscal_code += firstNameVowels[0]
        fiscal_code += firstNameVowels[1]
      } else if (firstNameConsonants.length == 1 && firstNameVowels.length == 1) {
        // add the first consonant, the first vowel and an "X"
        fiscal_code += firstNameConsonants[0]
        fiscal_code += firstNameVowels[0]
        fiscal_code += 'X'
      } else if (firstNameVowels.length == 2) {
        // add the first 2 vowels and an "X"
        fiscal_code += firstNameVowels[0]
        fiscal_code += firstNameVowels[1]
        fiscal_code += 'X'
      }

      // add the last 2 digits of the year of birth
      fiscal_code += birthDate?.getFullYear().toString().slice(-2)

      // add the month of birth
      fiscal_code += MONTH_TO_FISCAL_CODE[birthDate?.getMonth() ?? 0]

      // add the day of birth and the gender
      if (gender === GenderType.MALE) {
        fiscal_code += birthDate?.getDate().toString().padStart(2, '0')
      } else {
        // for females add 40 to the day of birth
        fiscal_code += ((birthDate?.getDate() ?? 0) + 40).toString().padStart(2, '0')
      }

      // add the cadastral code of the birth place
      const { data: municipalityData } = await getMunicipality({
        variables: {
          filter: {
            id: {
              eq: birthAddress?.municipalityId,
            },
          },
        },
      })

      const cadastralCode = municipalityData?.municipalities?.[0]?.cadastralCode
      if (!cadastralCode) {
        return {
          error: t('pms.common.customer.municipality_not_found'),
        }
      }
      fiscal_code += cadastralCode

      let total_sum = 0 // for each character inside the fiscal_code so far,
      // if the index is even then run ItalianFiscalCode_EvenCode on the element of the fiscal_code on the current index of the loop
      // else run ItalianFiscalCode_OddCode of that element
      for (let i = 0; i < 15; i++) {
        total_sum += i % 2 == 1 ? Number(ItalianFiscalCode_EvenCode(fiscal_code[i])) : Number(ItalianFiscalCode_OddCode(fiscal_code[i]))
      }

      const check_value = ALPHABET[total_sum % 26] // Get the remainder of the sum / 26 and use it as index to get the check value inside of alphabet

      if (fiscal_code.length != 15)
        return {
          error: t('pms.common.customer.something_went_wrong'),
        }
      return {
        fiscalCode: fiscal_code + check_value,
      }
    }
  }

  return generateItalianFiscalCode
}

// function took from HIC-1
function ItalianFiscalCode_EvenCode(n: string) {
  if (n == 'A' || n == '0') return 0
  if (n == 'B' || n == '1') return 1
  if (n == 'C' || n == '2') return 2
  if (n == 'D' || n == '3') return 3
  if (n == 'E' || n == '4') return 4
  if (n == 'F' || n == '5') return 5
  if (n == 'G' || n == '6') return 6
  if (n == 'H' || n == '7') return 7
  if (n == 'I' || n == '8') return 8
  if (n == 'J' || n == '9') return 9
  if (n == 'K') return 10
  if (n == 'L') return 11
  if (n == 'M') return 12
  if (n == 'N') return 13
  if (n == 'O') return 14
  if (n == 'P') return 15
  if (n == 'Q') return 16
  if (n == 'R') return 17
  if (n == 'S') return 18
  if (n == 'T') return 19
  if (n == 'U') return 20
  if (n == 'V') return 21
  if (n == 'W') return 22
  if (n == 'X') return 23
  if (n == 'Y') return 24
  if (n == 'Z') return 25
}

// function took from HIC-1
function ItalianFiscalCode_OddCode(c: string) {
  if (c == 'A' || c == '0') return 1
  if (c == 'B' || c == '1') return 0
  if (c == 'C' || c == '2') return 5
  if (c == 'D' || c == '3') return 7
  if (c == 'E' || c == '4') return 9
  if (c == 'F' || c == '5') return 13
  if (c == 'G' || c == '6') return 15
  if (c == 'H' || c == '7') return 17
  if (c == 'I' || c == '8') return 19
  if (c == 'J' || c == '9') return 21
  if (c == 'K') return 2
  if (c == 'L') return 4
  if (c == 'M') return 18
  if (c == 'N') return 20
  if (c == 'O') return 11
  if (c == 'P') return 3
  if (c == 'Q') return 6
  if (c == 'R') return 8
  if (c == 'S') return 12
  if (c == 'T') return 14
  if (c == 'U') return 16
  if (c == 'V') return 10
  if (c == 'W') return 22
  if (c == 'X') return 25
  if (c == 'Y') return 24
  if (c == 'Z') return 23
}
